import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import {
  SmartToy as AiIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SportIcon } from '../utils/sportIcons';
import { useRole } from '../contexts/RoleContext';
import { generatePick, canMakeRequest, getRemainingCooldown, testConnection } from '../services/claudeAiService';
import { createPickV2 } from '../services/handicapperPicksService';
import { fetchOdds, getSports } from '../services/oddsApi';

const BettorAIInterface = () => {
  const { currentUser } = useAuth();
  const { isAdmin } = useRole();

  // AI Personalities configuration
  const AI_PERSONALITIES = [
    {
      id: 'bettor-ai-con',
      name: 'BettorAICon',
      fullName: 'BettorAI Conservative',
      description: 'Conservative betting strategy focusing on safer, high-probability picks with mostly high confidence',
      color: '#1976d2',
      icon: 'shield',
      riskProfile: 'conservative'
    },
    {
      id: 'bettor-ai-agg',
      name: 'BettorAIAgg',
      fullName: 'BettorAI Aggressive',
      description: 'Aggressive betting strategy seeking high-value, high-reward opportunities with varied confidence',
      color: '#d32f2f',
      icon: 'flame',
      riskProfile: 'aggressive'
    },
    {
      id: 'bettor-ai-bal',
      name: 'BettorAIBal',
      fullName: 'BettorAI Balanced',
      description: 'Balanced betting strategy with evenly distributed confidence levels across all picks',
      color: '#388e3c',
      icon: 'scale',
      riskProfile: 'balanced'
    }
  ];

  // State management
  const [selectedPersonality, setSelectedPersonality] = useState(AI_PERSONALITIES[2]); // Default to Balanced
  const [selectedSport, setSelectedSport] = useState('');
  const [availableSports, setAvailableSports] = useState([]);
  const [availableGames, setAvailableGames] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [generatedPicks, setGeneratedPicks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingGames, setLoadingGames] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [canGenerate, setCanGenerate] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('unknown');

  // Check permissions - Admin only
  const hasAccess = isAdmin;

  // Update cooldown timer
  useEffect(() => {
    const updateCooldown = () => {
      const remaining = getRemainingCooldown();
      setCooldownTime(remaining);
      setCanGenerate(remaining === 0);
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Load available sports on mount
  useEffect(() => {
    const loadSports = async () => {
      try {
        const sports = await getSports();
        // Filter to only supported sports for BettorAI
        const supportedSports = sports.filter(sport =>
          ['americanfootball_nfl', 'americanfootball_ncaaf', 'basketball_nba',
            'basketball_ncaab', 'baseball_mlb', 'soccer_usa_mls'].includes(sport.key)
        );
        setAvailableSports(supportedSports);
      } catch (error) {
        console.error('Error loading sports:', error);
        setError('Failed to load available sports');
      }
    };

    loadSports();
  }, []);

  // Test connection on mount (only if not already tested in this session)
  useEffect(() => {
    const checkConnection = async () => {
      try {
        // Check if we already tested in this session (to avoid wasting API quota)
        const cachedStatus = sessionStorage.getItem('claude_connection_status');
        const cacheTime = sessionStorage.getItem('claude_connection_time');
        const tenMinutes = 10 * 60 * 1000;

        // Use cached result if less than 10 minutes old
        if (cachedStatus && cacheTime && (Date.now() - parseInt(cacheTime)) < tenMinutes) {
          console.log('🔍 Using cached connection status:', cachedStatus);
          setConnectionStatus(cachedStatus);
          if (cachedStatus === 'error') {
            setError('Connection failed. Click the status chip to retry.');
          }
          return;
        }

        // Otherwise test the connection
        const isConnected = await testConnection();
        const status = isConnected ? 'connected' : 'error';
        setConnectionStatus(status);

        // Cache the result
        sessionStorage.setItem('claude_connection_status', status);
        sessionStorage.setItem('claude_connection_time', Date.now().toString());

        if (!isConnected) {
          setError('Failed to connect to Claude AI. Click status to retry.');
        }
      } catch (err) {
        setConnectionStatus('error');
        setError(err.message || 'Failed to connect to Claude AI');
        // Cache the error state
        sessionStorage.setItem('claude_connection_status', 'error');
        sessionStorage.setItem('claude_connection_time', Date.now().toString());
      }
    };

    checkConnection();
  }, []);

  // Manual retry connection test
  const handleRetryConnection = async () => {
    setConnectionStatus('unknown');
    setError('');
    // Clear cache to force a new test
    sessionStorage.removeItem('claude_connection_status');
    sessionStorage.removeItem('claude_connection_time');

    try {
      const isConnected = await testConnection();
      const status = isConnected ? 'connected' : 'error';
      setConnectionStatus(status);
      sessionStorage.setItem('claude_connection_status', status);
      sessionStorage.setItem('claude_connection_time', Date.now().toString());

      if (!isConnected) {
        setError('Connection failed. Please check logs.');
      } else {
        setError('');
      }
    } catch (err) {
      setConnectionStatus('error');
      setError(err.message || 'Connection test failed');
      sessionStorage.setItem('claude_connection_status', 'error');
      sessionStorage.setItem('claude_connection_time', Date.now().toString());
    }
  };

  // Handle sport selection and load games
  const handleSportChange = async (sport) => {
    setSelectedSport(sport);
    setSelectedGames([]);
    setAvailableGames([]);
    setGeneratedPicks([]);
    setError('');

    if (!sport) return;

    setLoadingGames(true);
    try {
      const games = await fetchOdds(sport);
      // Filter to only upcoming games (not started)
      const upcomingGames = games.filter(game =>
        new Date(game.commence_time) > new Date()
      );
      setAvailableGames(upcomingGames);

      if (upcomingGames.length === 0) {
        setError('No upcoming games found for this sport.');
      }
    } catch (error) {
      console.error('Error loading games:', error);
      setError('Failed to load games for selected sport');
    } finally {
      setLoadingGames(false);
    }
  };

  // Handle individual game selection/deselection
  const handleGameToggle = (game) => {
    setSelectedGames(prev => {
      const isSelected = prev.find(g => g.id === game.id);
      if (isSelected) {
        return prev.filter(g => g.id !== game.id);
      } else {
        return [...prev, game];
      }
    });
  };

  const handleGeneratePicks = async () => {
    if (!canGenerate) {
      setError(`Please wait ${Math.ceil(cooldownTime / 1000)} seconds before generating another pick.`);
      return;
    }

    if (selectedGames.length === 0) {
      setError('Please select at least one game to analyze.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const picks = [];

      // Generate picks for each selected game
      for (const game of selectedGames) {
        try {
          const pick = await generatePick(game, currentUser.uid);
          picks.push({
            ...pick,
            status: 'generated',
            gameData: game // Keep original game data for reference
          });

          // Add small delay between requests to avoid rate limiting
          if (selectedGames.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (err) {
          console.error('Error generating pick for game:', game.id, err);
          picks.push({
            gameId: game.id,
            status: 'error',
            error: err.message || 'Failed to generate pick',
            gameData: game
          });
        }
      }

      setGeneratedPicks(picks);
      setConfirmDialogOpen(true);

    } catch (err) {
      console.error('Error in batch generation:', err);
      setError(err.message || 'Failed to generate picks');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmIndividualPick = async (pickIndex) => {
    const pick = generatedPicks[pickIndex];
    if (!pick || pick.status !== 'generated') return;

    // Update pick status to publishing
    setGeneratedPicks(prev =>
      prev.map((p, i) => i === pickIndex ? { ...p, status: 'publishing' } : p)
    );

    try {
      // Create the pick in Firestore with selected personality
      const pickData = {
        handicapperId: selectedPersonality.id,
        handicapperName: selectedPersonality.fullName,
        handicapperSlug: selectedPersonality.id,
        gameId: pick.gameId,
        sport: pick.sport,
        homeTeam: pick.homeTeam,
        awayTeam: pick.awayTeam,
        pickType: pick.pickType,
        pickedTeam: pick.pickedTeam,
        odds: pick.odds,
        confidence: pick.confidence,
        analysis: pick.analysis,
        bookmaker: pick.bookmaker,
        commenceTime: pick.commenceTime,
        aiGenerated: true,
        personality: selectedPersonality.id,
        personalityName: selectedPersonality.name,
        personalityColor: selectedPersonality.color,
        personalityIcon: selectedPersonality.icon
      };

      await createPickV2(pickData);

      // Update pick status to published
      setGeneratedPicks(prev =>
        prev.map((p, i) => i === pickIndex ? { ...p, status: 'published' } : p)
      );

      setSuccess('Pick has been successfully published!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Error saving pick:', err);
      // Update pick status back to generated with error
      setGeneratedPicks(prev =>
        prev.map((p, i) => i === pickIndex ? {
          ...p,
          status: 'generated',
          publishError: err.message
        } : p)
      );
      setError('Failed to save pick: ' + err.message);
    }
  };

  const handleConfirmAllPicks = async () => {
    const generatedPicksOnly = generatedPicks.filter(p => p.status === 'generated');

    for (let i = 0; i < generatedPicks.length; i++) {
      if (generatedPicks[i].status === 'generated') {
        await handleConfirmIndividualPick(i);
        // Add small delay between publishes
        if (i < generatedPicks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }
  };

  // Helper function to format game display
  const formatGameDisplay = (game) => {
    const gameTime = new Date(game.commence_time).toLocaleString();
    return `${game.away_team} @ ${game.home_team} - ${gameTime}`;
  };

  if (!hasAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. You need admin privileges to use BettorAI.
        </Alert>
      </Box>
    );
  }

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <AiIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          BettorAI Pick Generator
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analyze odds data and generate AI-powered betting picks
        </Typography>
      </Box>

      {/* Connection Status */}
      <Box sx={{ mb: 2 }}>
        <Chip
          icon={connectionStatus === 'connected' ? <CheckIcon /> : connectionStatus === 'unknown' ? <CircularProgress size={16} /> : <ErrorIcon />}
          label={
            connectionStatus === 'connected' ? 'Claude AI: Connected' :
              connectionStatus === 'unknown' ? 'Testing connection...' :
                'Claude AI: Click to retry'
          }
          color={connectionStatus === 'connected' ? 'success' : connectionStatus === 'unknown' ? 'default' : 'error'}
          size="small"
          onClick={connectionStatus !== 'connected' ? handleRetryConnection : undefined}
          sx={{
            cursor: connectionStatus !== 'connected' ? 'pointer' : 'default',
            '&:hover': connectionStatus !== 'connected' ? {
              opacity: 0.8
            } : {}
          }}
        />
        {connectionStatus === 'connected' && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            (Connection cached for 10 minutes)
          </Typography>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Input Section */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Game Selection
              </Typography>

              {/* Personality Selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>AI Personality</InputLabel>
                <Select
                  value={selectedPersonality.id}
                  onChange={(e) => {
                    const personality = AI_PERSONALITIES.find(p => p.id === e.target.value);
                    setSelectedPersonality(personality);
                  }}
                  label="AI Personality"
                >
                  {AI_PERSONALITIES.map((personality) => (
                    <MenuItem key={personality.id} value={personality.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: personality.color
                          }}
                        />
                        <Typography>{personality.fullName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({personality.riskProfile})
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Personality Description */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{selectedPersonality.fullName}</strong>: {selectedPersonality.description}
                </Typography>
              </Alert>

              {/* Sport Selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Sport</InputLabel>
                <Select
                  value={selectedSport}
                  onChange={(e) => handleSportChange(e.target.value)}
                  label="Select Sport"
                >
                  <MenuItem value="">
                    <em>Choose a sport...</em>
                  </MenuItem>
                  {availableSports.map((sport) => (
                    <MenuItem key={sport.key} value={sport.key}>
                      {sport.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Game Selection */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Select Games ({selectedGames.length} selected)
              </Typography>

              {loadingGames ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading games...</Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {availableGames.length === 0 && selectedSport ? (
                    <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      No upcoming games found for this sport.
                    </Typography>
                  ) : (
                    availableGames.map((game) => {
                      const isSelected = selectedGames.find(g => g.id === game.id);
                      return (
                        <Box
                          key={game.id}
                          sx={{
                            p: 2,
                            borderBottom: 1,
                            borderColor: 'divider',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? 'primary.light' : 'transparent',
                            '&:hover': { backgroundColor: isSelected ? 'primary.main' : 'grey.100' },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                          onClick={() => handleGameToggle(game)}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              border: 2,
                              borderColor: isSelected ? 'primary.main' : 'grey.400',
                              borderRadius: 1,
                              backgroundColor: isSelected ? 'primary.main' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {isSelected && <CheckIcon sx={{ color: 'white', fontSize: 16 }} />}
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                              {game.away_team} @ {game.home_team}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(game.commence_time).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              )}

              {/* Selected Games Summary */}
              {selectedGames.length > 0 && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Games ({selectedGames.length})
                  </Typography>
                  {selectedGames.map((game, index) => (
                    <Typography key={game.id} variant="body2" sx={{ opacity: 0.9 }}>
                      {index + 1}. {game.away_team} @ {game.home_team}
                    </Typography>
                  ))}
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleGeneratePicks}
                  disabled={loading || !canGenerate || connectionStatus !== 'connected' || selectedGames.length === 0}
                  startIcon={loading ? <CircularProgress size={20} /> : <AiIcon />}
                  fullWidth
                >
                  {loading ? `Generating ${selectedGames.length} picks...` : `Generate AI Picks (${selectedGames.length})`}
                </Button>
              </Box>              {/* Rate Limit Display */}
              {!canGenerate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ScheduleIcon color="warning" />
                  <Typography variant="body2" color="warning.main">
                    Next generation available in: {formatTime(cooldownTime)}
                  </Typography>
                </Box>
              )}

              {/* Messages */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                How to Use
              </Typography>

              <Typography variant="body2" paragraph>
                1. Select a sport from the dropdown menu
              </Typography>
              <Typography variant="body2" paragraph>
                2. Select one or multiple upcoming games
              </Typography>
              <Typography variant="body2" paragraph>
                3. Click "Generate AI Picks" to analyze the odds
              </Typography>
              <Typography variant="body2" paragraph>
                4. Review each generated pick in individual cards
              </Typography>
              <Typography variant="body2" paragraph>
                5. Confirm individual picks or publish all at once
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Features
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Live odds data from The Odds API
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • AI-powered analysis using Anthropic Claude
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Rate limiting to prevent API overuse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Automatic pick publication to featured picks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AiIcon color="primary" />
              BettorAI Generated Picks ({generatedPicks.length})
            </Box>
            <Button
              variant="contained"
              onClick={handleConfirmAllPicks}
              disabled={!generatedPicks.some(p => p.status === 'generated')}
              size="small"
            >
              Publish All
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            {generatedPicks.map((pick, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    border: 1,
                    borderColor: pick.status === 'error' ? 'error.main' :
                      pick.status === 'published' ? 'success.main' :
                        pick.status === 'publishing' ? 'warning.main' : 'divider'
                  }}
                >
                  <CardContent>
                    {pick.status === 'error' ? (
                      // Error state
                      <Box>
                        <Typography variant="h6" color="error" gutterBottom>
                          {pick.gameData.away_team} @ {pick.gameData.home_team}
                        </Typography>
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {pick.error}
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(pick.gameData.commence_time).toLocaleString()}
                        </Typography>
                      </Box>
                    ) : (
                      // Generated pick state
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {pick.awayTeam} @ {pick.homeTeam}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {new Date(pick.commenceTime).toLocaleString()}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          <Chip 
                            icon={<SportIcon sportKey={pick.sport} fontSize="small" />}
                            label={pick.sport} 
                            size="small" 
                          />
                          <Chip label={pick.pickType} size="small" />
                          <Chip
                            label={`${pick.confidence} confidence`}
                            color={
                              pick.confidence === 'high' ? 'success' :
                                pick.confidence === 'medium' ? 'warning' : 'default'
                            }
                            size="small"
                          />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">
                            Pick: {pick.pickedTeam}
                          </Typography>
                          <Typography variant="subtitle2">
                            Odds: {pick.odds > 0 ? '+' : ''}{pick.odds}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            Bookmaker: {pick.bookmaker}
                          </Typography>
                        </Box>

                        <Typography variant="body2" sx={{
                          fontSize: '0.85rem',
                          maxHeight: 100,
                          overflow: 'auto',
                          mb: 2,
                          p: 1,
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1
                        }}>
                          {pick.analysis}
                        </Typography>

                        {pick.publishError && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {pick.publishError}
                          </Alert>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            variant={pick.status === 'published' ? 'outlined' : 'contained'}
                            onClick={() => handleConfirmIndividualPick(index)}
                            disabled={pick.status !== 'generated'}
                            startIcon={
                              pick.status === 'publishing' ? <CircularProgress size={16} /> :
                                pick.status === 'published' ? <CheckIcon /> : null
                            }
                            color={pick.status === 'published' ? 'success' : 'primary'}
                          >
                            {pick.status === 'publishing' ? 'Publishing...' :
                              pick.status === 'published' ? 'Published' : 'Confirm & Publish'}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default BettorAIInterface;